/**
 * Responsibility: the grading page data.
 *
 * No polling at all, unlike every other page here. A restock is worthless
 * ninety seconds late; a grading decision is worth the same next week, and
 * eBay's sold-data quota is small enough that a background refetch would spend
 * it redrawing a page nobody is reading. The operator refreshes when they want
 * fresh numbers.
 *
 * Every mutation returns the whole board, so each one seeds the cache directly
 * rather than invalidating and refetching. That also avoids a flash of the old
 * ranking between the write landing and the read returning.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hubApi } from '../client';
import type {
  CandidateCreate,
  CandidatePatch,
  GradingBoard,
  PopulationInput,
} from '../client';

const BOARD_KEY = ['grading', 'board'];

export function useGradingBoard() {
  return useQuery({
    queryKey: BOARD_KEY,
    queryFn: hubApi.gradingBoard,
    // The numbers move slowly and the underlying quota does not. Refetching
    // on focus would turn tab-switching into API calls.
    refetchOnWindowFocus: false,
  });
}

/** Shared by every mutation below: the response is the new board. */
function useBoardMutation<Args>(fn: (args: Args) => Promise<GradingBoard>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (board) => client.setQueryData(BOARD_KEY, board),
  });
}

export function useAddCandidate() {
  return useBoardMutation((payload: CandidateCreate) => hubApi.addGradingCandidate(payload));
}

/** Promote a card already being watched on the discovery board. */
export function useAddFromWatched() {
  return useBoardMutation((discoveryKey: string) =>
    hubApi.addGradingFromWatched(discoveryKey),
  );
}

/** Promote a card liked on the search page. */
export function useAddFromLike() {
  return useBoardMutation((productKey: string) => hubApi.addGradingFromLike(productKey));
}

export function useRefreshGrading() {
  return useBoardMutation(() => hubApi.refreshGrading());
}

export function useRefreshCandidate() {
  return useBoardMutation((id: string) => hubApi.refreshGradingCandidate(id));
}

export function useSetPopulation() {
  return useBoardMutation((args: { id: string; population: PopulationInput }) =>
    hubApi.setGradingPopulation(args.id, args.population),
  );
}

export function usePatchCandidate() {
  return useBoardMutation((args: { id: string; patch: CandidatePatch }) =>
    hubApi.patchGradingCandidate(args.id, args.patch),
  );
}

export function useDeleteCandidate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hubApi.deleteGradingCandidate(id),
    // The only one that answers 204, so there is no board to seed.
    onSuccess: () => client.invalidateQueries({ queryKey: BOARD_KEY }),
  });
}
